import { Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import {
  Button,
  Form,
  InlineLoading,
  InlineNotification,
  Stack,
  TextInput,
  TextInputSkeleton,
  Toggle,
} from '@carbon/react';
import { useClearSavedState } from 'src/hooks/useClearSavedState';
import { useStorefront, useUpdateStorefrontMutation } from 'src/hooks/useStorefront';

/**
 * The same three patterns the API validates against, restated so a typo is a field error instead
 * of a 422 banner. They are the shapes each vendor's own console issues.
 *
 * An EMPTY box is always valid, because that is how a tag is cleared — `toAnalyticsPayload` maps
 * it to null, and without it an owner could add a tag but never remove one.
 *
 * `.optional()` is what actually permits the empty box, and the reason is Formik rather than Zod:
 * `prepareDataForValidation` rewrites every `''` to `undefined` before handing values to the
 * schema. So a plain `z.string()` reports "Required" for a field the user simply left blank —
 * Save silently does nothing while the field looks fine, because the message is attached to a
 * value the input is not showing. The `refine` still has to accept `''` for the direct-parse
 * path; it is `.optional()` that makes the form work.
 */
const blankOr = (pattern: RegExp) =>
  z
    .string()
    .refine((value) => value === '' || pattern.test(value))
    .optional();

const Schema = z.object({
  ga4MeasurementId: blankOr(/^G-[A-Z0-9]{4,12}$/),
  metaPixelId: blankOr(/^\d{15,16}$/),
  tiktokPixelId: blankOr(/^[A-Z0-9]{20}$/),
  consentRequired: z.boolean(),
});

export interface StorefrontAnalyticsValues {
  ga4MeasurementId: string;
  metaPixelId: string;
  tiktokPixelId: string;
  consentRequired: boolean;
}

/**
 * Empty string becomes null, which is the only way to say "remove this tag".
 *
 * The server merges a patch onto the stored value, so an ABSENT key means "leave it alone" —
 * exactly the trap `Appearance.tsx` documents for cleared images. Sending null is what actually
 * clears it.
 */
export function toAnalyticsPayload(values: StorefrontAnalyticsValues) {
  return {
    ga4MeasurementId: values.ga4MeasurementId.trim() || null,
    metaPixelId: values.metaPixelId.trim() || null,
    tiktokPixelId: values.tiktokPixelId.trim() || null,
    consentRequired: values.consentRequired,
  };
}

/**
 * Analytics tags, by id.
 *
 * There is deliberately no "paste your tracking code" box, and that is a security decision rather
 * than a simplification. Storefronts are served from `*.restavo.app`, the same registrable domain
 * as the portal, so the browser treats them as one site: a `<script>` an owner pasted could issue
 * credentialed requests to the API and ride the session of any portal user — or any Restavo
 * staffer — who visited that restaurant's site. An id that must match `^G-[A-Z0-9]{4,12}$` cannot
 * express code, so this form can be safe by construction rather than by sanitising.
 *
 * Google Tag Manager is absent for the same reason at one remove: it is a container that runs
 * whatever is configured inside Google's console, which would re-admit arbitrary script through
 * a field that looks like an id.
 */
const StorefrontAnalytics = React.memo(() => {
  const { t } = useTranslation();
  const { data: storefront, isLoading } = useStorefront();
  const {
    mutate: updateStorefront,
    isLoading: saving,
    isSuccess: saved,
    error: updateError,
    reset: resetUpdate,
  } = useUpdateStorefrontMutation();

  useClearSavedState(saved, resetUpdate);

  const analytics = (storefront?.analytics ?? {}) as Partial<StorefrontAnalyticsValues>;

  return (
    <Stack gap={6}>
      <p className="storefront-analytics__hint">{t('storefront.analytics.hint')}</p>
      {isLoading && <TextInputSkeleton />}
      {!isLoading && storefront && (
        <Formik<StorefrontAnalyticsValues>
          enableReinitialize
          validationSchema={toFormikValidationSchema(Schema)}
          initialValues={{
            ga4MeasurementId: analytics.ga4MeasurementId ?? '',
            metaPixelId: analytics.metaPixelId ?? '',
            tiktokPixelId: analytics.tiktokPixelId ?? '',
            // `!== false`, not `?? true`: an unconfigured storefront must land on "consent
            // required", and only an explicit false turns it off.
            consentRequired: analytics.consentRequired !== false,
          }}
          onSubmit={(values) => updateStorefront({ analytics: toAnalyticsPayload(values) } as never)}
        >
          {({ handleSubmit, values, errors, setFieldValue, handleChange, isValid }) => (
            <Form onSubmit={handleSubmit}>
              <Stack gap={6}>
                <TextInput
                  id="ga4MeasurementId"
                  name="ga4MeasurementId"
                  labelText={t('storefront.analytics.ga4')}
                  helperText={t('storefront.analytics.ga4Hint')}
                  value={values.ga4MeasurementId}
                  invalid={Boolean(errors.ga4MeasurementId)}
                  invalidText={t('storefront.analytics.ga4Invalid')}
                  onChange={handleChange}
                />

                <TextInput
                  id="metaPixelId"
                  name="metaPixelId"
                  labelText={t('storefront.analytics.metaPixel')}
                  helperText={t('storefront.analytics.metaPixelHint')}
                  value={values.metaPixelId}
                  invalid={Boolean(errors.metaPixelId)}
                  invalidText={t('storefront.analytics.metaPixelInvalid')}
                  onChange={handleChange}
                />

                <TextInput
                  id="tiktokPixelId"
                  name="tiktokPixelId"
                  labelText={t('storefront.analytics.tiktokPixel')}
                  helperText={t('storefront.analytics.tiktokPixelHint')}
                  value={values.tiktokPixelId}
                  invalid={Boolean(errors.tiktokPixelId)}
                  invalidText={t('storefront.analytics.tiktokPixelInvalid')}
                  onChange={handleChange}
                />

                {/* Turning this off is the owner asserting they have their own lawful basis for
                    processing — it does not make the obligation go away, which is what the hint
                    says in both locales. */}
                <Toggle
                  id="consentRequired"
                  labelText={t('storefront.analytics.consent')}
                  labelA={t('storefront.analytics.consentOff')}
                  labelB={t('storefront.analytics.consentOn')}
                  toggled={values.consentRequired}
                  onToggle={(checked: boolean) => setFieldValue('consentRequired', checked)}
                />
                <p className="cds--form__helper-text">{t('storefront.analytics.consentHint')}</p>

                <div className="storefront-analytics__actions">
                  {saving || saved ? (
                    <InlineLoading
                      description={saved ? t('common.saved') : `${t('common.saving')}...`}
                      status={saved ? 'finished' : 'active'}
                    />
                  ) : (
                    <Button type="submit" disabled={!isValid}>
                      {t('storefront.actions.save')}
                    </Button>
                  )}
                </div>

                {updateError ? <InlineNotification kind="error" subtitle={t('messages.somethingWentWrong')} /> : null}
              </Stack>
            </Form>
          )}
        </Formik>
      )}
    </Stack>
  );
});

export default StorefrontAnalytics;
